/* A project or a tag as a scoped Plan — the container (or facet) axis. The
   title carries the project's ring, the way the mobile header does, and the
   page is washed in the scope's own hue. */
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { overdueLabel, type Scope, todayStr, useStore } from '@habishine/client-core';

import { Capture } from '../components/Capture';
import { Icon } from '../components/Icon';
import { Ring } from '../components/Ring';
import { CardGroup, DoneRow, GhostAdd, SectionHead, SkippedRow, TaskRow, Toggle } from '../components/rows';
import { SortableList } from '../components/Sortable';
import { Button, Page, PageHeader } from '../components/ui';
import { CAPTURE_INPUT_ID } from '../components/Capture';
import { useEditors } from '../shell/Editors';
import { hueVar } from '../theme/hues';

export function ScopePage({ kind }: { kind: Scope['kind'] }) {
  const { id = '' } = useParams();
  const store = useStore();
  const { editProject, editTag, openTask } = useEditors();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const row = kind === 'project' ? store.state.projects.find((p) => p.id === id) : store.state.tags.find((t) => t.id === id);
  if (!row) return <Navigate to="/today" replace />;
  const scope: Scope = { kind, id };
  const tasks = store.tasksInScope(scope);
  const open = tasks.filter((t) => !t.done && !t.skipped);
  const resolved = tasks.filter((t) => t.done || t.skipped);
  const progress = store.scopeProgress(scope);
  const today = todayStr();

  return (
    <Page className="screen-in">
      <PageHeader
        hue={row.hue}
        eyebrow={`${kind === 'project' ? 'Project' : 'Tag'} · ${progress.done} of ${progress.total} done`}
        title={row.name}
        titleAccessory={
          kind === 'project' ? (
            progress.total > 0 ? <Ring size={30} stroke={4} hue={row.hue} pct={progress.done / progress.total} check={progress.done === progress.total} /> : <span className="h-4 w-4 rounded-full" style={{ background: hueVar(row.hue, 'c') }} />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-[12px]" style={{ background: hueVar(row.hue, 't'), color: hueVar(row.hue, 'k') }}><Icon name="tag" size={20} strokeWidth={2.2} /></span>
          )
        }
        right={
          <>
            <Button size="sm" onClick={() => (kind === 'project' ? editProject(id) : editTag(id))}><Icon name="edit" size={14} /> Edit</Button>
            {kind === 'project' ? <Button size="sm" onClick={() => { store.setProjectArchived(id, true); navigate('/plan'); }}>Archive</Button> : null}
            {confirming ? (
              <>
                <Button size="sm" variant="danger" onClick={() => { if (kind === 'project') store.deleteProject(id); else store.deleteTag(id); navigate('/plan'); }}>Delete (tasks stay)</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Keep</Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => setConfirming(true)}><Icon name="trash" size={15} /></Button>
            )}
          </>
        }
      />
      <Capture defaultToday={false} projectId={kind === 'project' ? id : null} placeholder={kind === 'project' ? `Add to ${row.name} — "tomorrow", "@tag"` : `Add a task — type @${row.name} to tag it`} />

      <SectionHead count={open.length}>Open</SectionHead>
      {open.length === 0 ? (
        <GhostAdd label="Nothing open here — add something" onClick={() => document.getElementById(CAPTURE_INPUT_ID)?.focus()} />
      ) : (
        <CardGroup index={1}>
          <SortableList
            items={open}
            onMove={(from, to) => store.moveTask(open, from, to)}
            render={(t, i) => (
              <TaskRow
                task={t}
                first={i === 0}
                index={i + 1}
                hue={store.hueForTask(t)}
                projectNote={kind === 'tag' ? store.projectForTask(t)?.name : undefined}
                tagNote={kind === 'project' ? store.tagsOfTask(t).map((g) => g.name) : undefined}
                dateNote={t.scheduledFor ? (t.scheduledFor < today ? overdueLabel(t.scheduledFor) : t.scheduledFor === today ? 'Today' : t.scheduledFor) : undefined}
                dateTone={t.scheduledFor && t.scheduledFor < today ? 'replan' : 'meta'}
                onOpen={() => openTask(t.id, t.scheduledFor ?? today)}
                onComplete={() => store.completeItem(t.id)}
              />
            )}
          />
        </CardGroup>
      )}
      {resolved.length > 0 ? (
        <>
          <Toggle label={`Resolved (${resolved.length})`} open={resolvedOpen} onToggle={() => setResolvedOpen((o) => !o)} />
          {resolvedOpen ? (
            <CardGroup>
              {resolved.map((t, i) =>
                t.done ? <DoneRow key={t.id} task={t} hue={store.hueForTask(t)} first={i === 0} note={t.doneAt ? overdueLabel(t.doneAt) : undefined} onOpen={() => openTask(t.id, t.doneAt ?? today)} />
                : <SkippedRow key={t.id} task={t} hue={store.hueForTask(t)} first={i === 0} onOpen={() => openTask(t.id, t.skippedAt ?? today)} />,
              )}
            </CardGroup>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}
